import React, { useEffect, useRef } from 'react';
import * as THREE from 'three';
import { ATLANTA_ROUTES, BOARD_RADIUS, LANE_COUNT, LANE_NEON, LANE_WIDTH, MAP_LABELS, categoryColor, createLabelTexture, createLaneRibbonGeometry, createPeachtreeCurve, createPerimeterCurve, createRouteCurve, createSkyTexture, isClearOfRoads, playerColor, sampleCurvePoints, tileProgress } from './boardGeometry';
export interface ThreeBoardPlayer {
  id: string;
  color: string;
  position: number;
  pathIndex: number | null;
}
interface ThreeBoardProps {
  players: ThreeBoardPlayer[];
  activePathTiles: number[][];
  currentPlayerIndex: number;
  /** null = metro overview, 0..3 = follow that corridor's active traveler. */
  focusedPathIndex: number | null;
  getTileCategory: (tileId: number) => string;
}
const MAX_TOKENS = 6;
// Slow enough for every tile to register as its own move, while still keeping
// a six-space roll brisk.
const TILES_PER_SECOND = 2.8;
const TOKEN_HOP_HEIGHT = 1.1;
interface FrameState {
  players: ThreeBoardPlayer[];
  currentPlayerIndex: number;
  focusedPathIndex: number | null;
}
export function ThreeBoard({
  players,
  activePathTiles,
  currentPlayerIndex,
  focusedPathIndex,
  getTileCategory
}: ThreeBoardProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const frameStateRef = useRef<FrameState>({
    players,
    currentPlayerIndex,
    focusedPathIndex
  });
  const categoryLookupRef = useRef(getTileCategory);
  frameStateRef.current = {
    players,
    currentPlayerIndex,
    focusedPathIndex
  };
  categoryLookupRef.current = getTileCategory;

  // Tile counts only change on a career switch, so the map is rebuilt against
  // this key rather than on every game-state tick.
  const laneKey = activePathTiles.map(lane => lane.length).join('-');
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const prefersReducedMotion = typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const isCompact = container.clientWidth < 820 || typeof navigator !== 'undefined' && typeof navigator.hardwareConcurrency === 'number' && navigator.hardwareConcurrency <= 4;

    // ── Renderer ────────────────────────────────────────────────────────────
    const renderer = new THREE.WebGLRenderer({
      antialias: !isCompact,
      powerPreference: 'high-performance'
    });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, isCompact ? 1.5 : 2));
    renderer.setSize(container.clientWidth, container.clientHeight);
    renderer.shadowMap.enabled = !isCompact;
    renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    renderer.domElement.style.display = 'block';
    renderer.domElement.style.width = '100%';
    renderer.domElement.style.height = '100%';
    container.appendChild(renderer.domElement);
    const disposables: Array<{
      dispose: () => void;
    }> = [];
    const track = <T extends {
      dispose: () => void;
    },>(item: T): T => {
      disposables.push(item);
      return item;
    };

    // ── Scene, camera, atmosphere ───────────────────────────────────────────
    const scene = new THREE.Scene();
    const skyTexture = track(createSkyTexture());
    scene.background = skyTexture;
    scene.fog = new THREE.Fog(0x171334, 150, 330);
    const camera = new THREE.PerspectiveCamera(isCompact ? 54 : 46, container.clientWidth / Math.max(1, container.clientHeight), 0.1, 620);

    // ── Lighting ────────────────────────────────────────────────────────────
    scene.add(new THREE.HemisphereLight(0xbcd0ff, 0x1b1330, 1.1));
    const keyLight = new THREE.DirectionalLight(0xfff1dc, 1.3);
    keyLight.position.set(60, 110, 70);
    if (!isCompact) {
      keyLight.castShadow = true;
      keyLight.shadow.mapSize.set(1024, 1024);
      keyLight.shadow.camera.near = 10;
      keyLight.shadow.camera.far = 320;
      keyLight.shadow.camera.left = -130;
      keyLight.shadow.camera.right = 130;
      keyLight.shadow.camera.top = 130;
      keyLight.shadow.camera.bottom = -130;
    }
    scene.add(keyLight);
    const downtownGlow = new THREE.PointLight(0xffc27a, 140, 120);
    downtownGlow.position.set(0, 26, 0);
    scene.add(downtownGlow);

    // ── Terrain and metro base ──────────────────────────────────────────────
    const ground = new THREE.Mesh(track(new THREE.PlaneGeometry(620, 620)), track(new THREE.MeshStandardMaterial({
      color: 0x1f3f31,
      roughness: 0.98,
      metalness: 0
    })));
    ground.rotation.x = -Math.PI / 2;
    ground.position.y = -1.2;
    ground.receiveShadow = !isCompact;
    scene.add(ground);
    const metroBase = new THREE.Mesh(track(new THREE.CylinderGeometry(BOARD_RADIUS, BOARD_RADIUS + 5, 2.4, 72)), track(new THREE.MeshStandardMaterial({
      color: 0x2b5140,
      roughness: 0.95,
      metalness: 0.04
    })));
    metroBase.position.y = -1.1;
    metroBase.receiveShadow = !isCompact;
    scene.add(metroBase);

    // ── Roads ───────────────────────────────────────────────────────────────
    const asphaltMaterial = track(new THREE.MeshStandardMaterial({
      color: 0x3a3746,
      roughness: 0.86,
      metalness: 0.1,
      side: THREE.DoubleSide
    }));
    const laneStripeMaterial = track(new THREE.MeshBasicMaterial({
      color: 0xf8e9a8,
      toneMapped: false
    }));
    const surfaceRoadMaterial = track(new THREE.MeshStandardMaterial({
      color: 0x4a4656,
      roughness: 0.94
    }));
    const perimeterCurve = createPerimeterCurve();
    const peachtreeCurve = createPeachtreeCurve();

    // I-285 Perimeter: the loop that frames the whole metro.
    const perimeter = new THREE.Mesh(track(createLaneRibbonGeometry(perimeterCurve, 7.4, isCompact ? 90 : 170)), asphaltMaterial);
    perimeter.position.y = 0.05;
    perimeter.receiveShadow = !isCompact;
    scene.add(perimeter);
    const perimeterStripe = new THREE.Mesh(track(createLaneRibbonGeometry(perimeterCurve, 0.3, isCompact ? 90 : 170)), laneStripeMaterial);
    perimeterStripe.position.y = 0.12;
    scene.add(perimeterStripe);

    // Peachtree Street: the diagonal surface spine.
    const peachtree = new THREE.Mesh(track(createLaneRibbonGeometry(peachtreeCurve, 3.6, isCompact ? 50 : 90)), surfaceRoadMaterial);
    peachtree.position.y = 0.06;
    peachtree.receiveShadow = !isCompact;
    scene.add(peachtree);

    // Neighborhood street grid, clipped to the metro base.
    const gridMaterial = track(new THREE.MeshStandardMaterial({
      color: 0x474357,
      roughness: 0.95
    }));
    const gridGeometry = track(new THREE.BoxGeometry(1, 0.1, 1));
    for (let i = -3; i <= 3; i++) {
      if (i === 0) continue;
      const offset = i * 24;
      const halfSpan = Math.sqrt(Math.max(0, BOARD_RADIUS * BOARD_RADIUS - offset * offset));
      if (halfSpan < 12) continue;
      const eastWest = new THREE.Mesh(gridGeometry, gridMaterial);
      eastWest.position.set(0, 0.02, offset);
      eastWest.scale.set(halfSpan * 2 - 8, 1, 2.4);
      scene.add(eastWest);
      const northSouth = new THREE.Mesh(gridGeometry, gridMaterial);
      northSouth.position.set(offset, 0.02, 0);
      northSouth.scale.set(2.4, 1, halfSpan * 2 - 8);
      scene.add(northSouth);
    }

    // ── Player corridors, tiles and overpass supports ───────────────────────
    const curves = Array.from({
      length: LANE_COUNT
    }, (_, lane) => createRouteCurve(lane));
    const tileMeshes: THREE.Mesh[][] = [];
    const tileGeometry = track(new THREE.CylinderGeometry(1.3, 1.45, 0.4, 10));
    const tileMaterials = new Map<string, THREE.MeshStandardMaterial>();
    const materialForCategory = (category: string) => {
      const existing = tileMaterials.get(category);
      if (existing) return existing;
      const hex = categoryColor(category);
      const material = track(new THREE.MeshStandardMaterial({
        color: new THREE.Color(hex),
        emissive: new THREE.Color(hex),
        emissiveIntensity: 0.5,
        roughness: 0.4,
        metalness: 0.15
      }));
      tileMaterials.set(category, material);
      return material;
    };
    const pillarGeometry = track(new THREE.BoxGeometry(1.5, 1, 1.5));
    const pillarMaterial = track(new THREE.MeshStandardMaterial({
      color: 0x53506b,
      roughness: 0.9
    }));
    curves.forEach((curve, lane) => {
      const tiles = activePathTiles[lane] ?? [];
      const segments = isCompact ? 70 : 130;
      const road = new THREE.Mesh(track(createLaneRibbonGeometry(curve, LANE_WIDTH, segments)), asphaltMaterial);
      road.position.y = 0.16;
      road.receiveShadow = !isCompact;
      road.castShadow = !isCompact;
      scene.add(road);

      // Route-colored edge lines identify which corridor belongs to which path.
      const neon = new THREE.Color(LANE_NEON[lane] ?? '#a855f7');
      const edgeMaterial = track(new THREE.MeshBasicMaterial({
        color: neon,
        toneMapped: false
      }));
      const edge = new THREE.Mesh(track(createLaneRibbonGeometry(curve, LANE_WIDTH + 1.1, segments)), edgeMaterial);
      edge.position.y = 0.1;
      scene.add(edge);

      // Center dashes read as real freeway lane markings.
      const dashGeometry = track(new THREE.BoxGeometry(0.24, 0.06, 2.4));
      const dashCount = isCompact ? 14 : 26;
      for (let i = 0; i < dashCount; i++) {
        const u = (i + 0.5) / dashCount;
        const point = curve.getPointAt(u);
        const tangent = curve.getTangentAt(u);
        const dash = new THREE.Mesh(dashGeometry, laneStripeMaterial);
        dash.position.set(point.x, point.y + 0.24, point.z);
        dash.rotation.y = Math.atan2(tangent.x, tangent.z);
        scene.add(dash);
      }

      // Where a corridor is elevated it becomes a bridge: add support columns
      // and guardrails so the Downtown crossovers are unmistakable.
      const supportSamples = isCompact ? 26 : 46;
      for (let i = 0; i <= supportSamples; i++) {
        const point = curve.getPointAt(i / supportSamples);
        if (point.y < 0.9) continue;
        const pillar = new THREE.Mesh(pillarGeometry, pillarMaterial);
        pillar.position.set(point.x, point.y / 2, point.z);
        pillar.scale.set(1, Math.max(0.6, point.y), 1);
        pillar.castShadow = !isCompact;
        scene.add(pillar);
      }
      const laneTiles: THREE.Mesh[] = [];
      tiles.forEach((tileId, index) => {
        const category = categoryLookupRef.current(tileId);
        const mesh = new THREE.Mesh(tileGeometry, materialForCategory(category));
        const point = curve.getPointAt(tileProgress(index, tiles.length));
        mesh.position.set(point.x, point.y + 0.42, point.z);
        mesh.receiveShadow = !isCompact;
        scene.add(mesh);
        laneTiles.push(mesh);
      });
      tileMeshes.push(laneTiles);
    });

    // ── Scenery placement keeps clear of every road surface ─────────────────
    const roadPoints = sampleCurvePoints([...curves, perimeterCurve, peachtreeCurve], isCompact ? 40 : 70);

    // Downtown skyline: dense, tall, brightly lit at the map's core.
    const buildingGeometry = track(new THREE.BoxGeometry(1, 1, 1));
    const roofGeometry = track(new THREE.ConeGeometry(1, 0.8, 4));
    const towerMaterials = ['#3b3563', '#463a6d', '#334066', '#5b3c66'].map(hex => track(new THREE.MeshStandardMaterial({
      color: hex,
      roughness: 0.74,
      metalness: 0.16,
      emissive: new THREE.Color(hex),
      emissiveIntensity: 0.16
    })));
    const roofMaterial = track(new THREE.MeshStandardMaterial({
      color: 0xe7a2c6,
      roughness: 0.7
    }));
    const windowMaterial = track(new THREE.MeshBasicMaterial({
      color: 0xffe3a4,
      toneMapped: false
    }));
    const placeBuilding = (x: number, z: number, width: number, height: number, depth: number, index: number) => {
      const body = new THREE.Mesh(buildingGeometry, towerMaterials[index % towerMaterials.length]);
      body.position.set(x, height / 2, z);
      body.scale.set(width, height, depth);
      body.castShadow = !isCompact;
      body.receiveShadow = !isCompact;
      scene.add(body);
      if (height < 14) {
        const roof = new THREE.Mesh(roofGeometry, roofMaterial);
        roof.position.set(x, height + 1.2, z);
        roof.scale.set(width * 0.8, 3, depth * 0.8);
        roof.rotation.y = Math.PI / 4;
        scene.add(roof);
      }
      const rows = Math.min(5, Math.floor(height / 5));
      for (let row = 0; row < rows; row++) {
        const windows = new THREE.Mesh(track(new THREE.BoxGeometry(width * 0.66, 0.7, 0.1)), windowMaterial);
        windows.position.set(x, 3 + row * 4.4, z + depth / 2 + 0.06);
        scene.add(windows);
      }
    };
    const downtownCount = isCompact ? 12 : 26;
    let placedDowntown = 0;
    for (let i = 0; i < downtownCount * 4 && placedDowntown < downtownCount; i++) {
      const angle = i * 2.399;
      const radius = 12 + i * 7 % 24;
      const x = Math.cos(angle) * radius;
      const z = Math.sin(angle) * radius;
      if (!isClearOfRoads(x, z, roadPoints, 8.5)) continue;
      placeBuilding(x, z, 4.5 + i * 3 % 4, 20 + i * 13 % 30, 4.5 + i * 5 % 4, i);
      placedDowntown++;
    }

    // Surrounding neighborhoods: lower, sparser, with parks and tree cover.
    const neighborhoodCount = isCompact ? 14 : 34;
    let placedNeighborhood = 0;
    for (let i = 0; i < neighborhoodCount * 5 && placedNeighborhood < neighborhoodCount; i++) {
      const angle = i * 1.117;
      const radius = 38 + i * 11 % 52;
      const x = Math.cos(angle) * radius;
      const z = Math.sin(angle) * radius;
      if (x * x + z * z > (BOARD_RADIUS - 12) * (BOARD_RADIUS - 12)) continue;
      if (!isClearOfRoads(x, z, roadPoints, 9.5)) continue;
      placeBuilding(x, z, 5 + i * 3 % 5, 6 + i * 7 % 12, 5 + i * 5 % 5, i + 2);
      placedNeighborhood++;
    }
    const treeCount = isCompact ? 40 : 110;
    const trunkGeometry = track(new THREE.CylinderGeometry(0.26, 0.4, 2.4, 6));
    const crownGeometry = track(new THREE.ConeGeometry(1.9, 5, 7));
    const trunkMaterial = track(new THREE.MeshStandardMaterial({
      color: 0x6b452f,
      roughness: 0.96
    }));
    const crownMaterial = track(new THREE.MeshStandardMaterial({
      color: 0x2f8b57,
      roughness: 0.92
    }));
    const trunks = new THREE.InstancedMesh(trunkGeometry, trunkMaterial, treeCount);
    const crowns = new THREE.InstancedMesh(crownGeometry, crownMaterial, treeCount);
    crowns.castShadow = !isCompact;
    const dummy = new THREE.Object3D();
    let placedTrees = 0;
    for (let i = 0; i < treeCount * 6 && placedTrees < treeCount; i++) {
      const angle = i * 0.917;
      const radius = 16 + i * 13 % 84;
      const x = Math.cos(angle) * radius;
      const z = Math.sin(angle) * radius;
      if (x * x + z * z > (BOARD_RADIUS - 6) * (BOARD_RADIUS - 6)) continue;
      if (!isClearOfRoads(x, z, roadPoints, 7)) continue;
      const scale = 0.7 + i * 7 % 6 * 0.09;
      dummy.position.set(x, 1.2 * scale, z);
      dummy.scale.setScalar(scale);
      dummy.rotation.y = i * 0.4;
      dummy.updateMatrix();
      trunks.setMatrixAt(placedTrees, dummy.matrix);
      dummy.position.set(x, 4.3 * scale, z);
      dummy.updateMatrix();
      crowns.setMatrixAt(placedTrees, dummy.matrix);
      placedTrees++;
    }
    trunks.count = placedTrees;
    crowns.count = placedTrees;
    trunks.instanceMatrix.needsUpdate = true;
    crowns.instanceMatrix.needsUpdate = true;
    scene.add(trunks, crowns);

    // ── Freeway shields and place names ─────────────────────────────────────
    const labelSprites: THREE.Sprite[] = [];
    MAP_LABELS.forEach(label => {
      const material = track(new THREE.SpriteMaterial({
        map: track(createLabelTexture(label)),
        transparent: true,
        depthTest: false
      }));
      const sprite = new THREE.Sprite(material);
      const scale = label.kind === 'place' ? 20 : 11;
      sprite.scale.set(scale, scale / 2, 1);
      sprite.position.set(label.x, label.kind === 'place' ? 15 : 11, label.z);
      sprite.renderOrder = 5;
      scene.add(sprite);
      labelSprites.push(sprite);
    });

    // Downtown billboards keep the neon energy of the original board.
    const billboardLights: THREE.PointLight[] = [];
    const billboardConfigs = [{
      color: 0xec4899,
      x: -20,
      z: 16
    }, {
      color: 0xf59e0b,
      x: 22,
      z: -18
    }, {
      color: 0x8b5cf6,
      x: 18,
      z: 22
    }];
    billboardConfigs.forEach(config => {
      const light = new THREE.PointLight(config.color, isCompact ? 14 : 26, 42);
      light.position.set(config.x, 14, config.z);
      scene.add(light);
      billboardLights.push(light);
    });

    // ── Player tokens ───────────────────────────────────────────────────────
    const tokenBodyGeometry = track(new THREE.CapsuleGeometry(0.8, 1.2, 4, 12));
    const tokenBaseGeometry = track(new THREE.CylinderGeometry(1.05, 1.2, 0.3, 14));
    const ringGeometry = track(new THREE.TorusGeometry(1.55, 0.12, 8, 26));
    interface Token {
      group: THREE.Group;
      bodyMaterial: THREE.MeshStandardMaterial;
      baseMaterial: THREE.MeshStandardMaterial;
      ringMaterial: THREE.MeshBasicMaterial;
      ring: THREE.Mesh;
      displayPosition: number;
      colorKey: string;
    }
    const tokens: Token[] = [];
    for (let i = 0; i < MAX_TOKENS; i++) {
      const group = new THREE.Group();
      const bodyMaterial = track(new THREE.MeshStandardMaterial({
        color: 0xffffff,
        roughness: 0.32,
        metalness: 0.28,
        emissive: new THREE.Color(0x000000),
        emissiveIntensity: 0.4
      }));
      const body = new THREE.Mesh(tokenBodyGeometry, bodyMaterial);
      body.position.y = 1.55;
      body.castShadow = !isCompact;
      group.add(body);
      const baseMaterial = track(new THREE.MeshStandardMaterial({
        color: 0xffffff,
        roughness: 0.5,
        metalness: 0.2
      }));
      const base = new THREE.Mesh(tokenBaseGeometry, baseMaterial);
      base.position.y = 0.3;
      base.castShadow = !isCompact;
      group.add(base);
      const ringMaterial = track(new THREE.MeshBasicMaterial({
        color: 0xffffff,
        transparent: true,
        opacity: 0,
        toneMapped: false
      }));
      const ring = new THREE.Mesh(ringGeometry, ringMaterial);
      ring.rotation.x = -Math.PI / 2;
      ring.position.y = 0.14;
      group.add(ring);
      group.visible = false;
      scene.add(group);
      tokens.push({
        group,
        bodyMaterial,
        baseMaterial,
        ringMaterial,
        ring,
        displayPosition: 0,
        colorKey: ''
      });
    }

    // ── Camera rig ──────────────────────────────────────────────────────────
    // Overview frames the whole metro like a map; focus drops toward the road.
    const overviewPosition = new THREE.Vector3(0, isCompact ? 210 : 168, isCompact ? 150 : 128);
    const overviewTarget = new THREE.Vector3(0, 0, -4);
    const desiredPosition = overviewPosition.clone();
    const desiredTarget = overviewTarget.clone();
    const currentTarget = overviewTarget.clone();
    camera.position.copy(overviewPosition);
    camera.lookAt(currentTarget);
    const scratchPoint = new THREE.Vector3();
    const scratchTangent = new THREE.Vector3();
    const scratchSide = new THREE.Vector3();
    const worldUp = new THREE.Vector3(0, 1, 0);
    let pulsedTile: THREE.Mesh | null = null;
    const clock = new THREE.Clock();
    let animationId = 0;
    const renderFrame = () => {
      animationId = requestAnimationFrame(renderFrame);
      const delta = Math.min(clock.getDelta(), 0.05);
      const elapsed = clock.elapsedTime;
      const state = frameStateRef.current;
      if (!prefersReducedMotion) {
        billboardLights.forEach((light, index) => {
          light.intensity = (isCompact ? 14 : 26) + Math.sin(elapsed * 2 + index * 1.7) * 4;
        });
      }

      // Tokens travel their Atlanta corridor one tile at a time.
      tokens.forEach((token, index) => {
        const player = state.players[index];
        if (!player || player.pathIndex === null) {
          token.group.visible = false;
          return;
        }
        const lane = Math.min(Math.max(player.pathIndex, 0), LANE_COUNT - 1);
        const laneTiles = activePathTiles[lane] ?? [];
        const curve = curves[lane];
        if (!curve || laneTiles.length === 0) {
          token.group.visible = false;
          return;
        }
        token.group.visible = true;
        if (player.color !== token.colorKey) {
          const hex = new THREE.Color(playerColor(player.color));
          token.bodyMaterial.color.copy(hex);
          token.bodyMaterial.emissive.copy(hex);
          token.baseMaterial.color.copy(hex);
          token.ringMaterial.color.copy(hex);
          token.colorKey = player.color;
        }
        const target = player.position;
        if (prefersReducedMotion) {
          token.displayPosition = target;
        } else if (token.displayPosition !== target) {
          const step = TILES_PER_SECOND * delta;
          const gap = target - token.displayPosition;
          token.displayPosition = Math.abs(gap) <= step ? target : token.displayPosition + Math.sign(gap) * step;
        }
        const u = tileProgress(token.displayPosition, laneTiles.length);
        curve.getPointAt(u, scratchPoint);
        curve.getTangentAt(u, scratchTangent);
        const fraction = token.displayPosition - Math.floor(token.displayPosition);
        const hop = prefersReducedMotion || fraction === 0 ? 0 : Math.sin(fraction * Math.PI) * TOKEN_HOP_HEIGHT;

        // Offset across the roadway so tokens sharing a corridor stay readable.
        scratchSide.crossVectors(scratchTangent, worldUp).normalize();
        const lateral = (index - (state.players.length - 1) / 2) * 1.3;
        token.group.position.set(scratchPoint.x + scratchSide.x * lateral, scratchPoint.y + 0.35 + hop, scratchPoint.z + scratchSide.z * lateral);
        token.group.rotation.y = Math.atan2(scratchTangent.x, scratchTangent.z) + Math.PI;
        const isActive = index === state.currentPlayerIndex;
        token.ringMaterial.opacity = isActive ? prefersReducedMotion ? 0.8 : 0.75 + Math.sin(elapsed * 4) * 0.2 : 0;
        token.ring.scale.setScalar(isActive && !prefersReducedMotion ? 1 + Math.sin(elapsed * 4) * 0.06 : 1);
      });

      // The tile under the active player pulses in its own category color.
      if (pulsedTile) {
        pulsedTile.scale.setScalar(1);
        pulsedTile = null;
      }
      const activePlayer = state.players[state.currentPlayerIndex];
      if (activePlayer && activePlayer.pathIndex !== null) {
        const laneTiles = tileMeshes[activePlayer.pathIndex] ?? [];
        const mesh = laneTiles[activePlayer.position];
        if (mesh) {
          pulsedTile = mesh;
          mesh.scale.setScalar(prefersReducedMotion ? 1.12 : 1.12 + Math.sin(elapsed * 5) * 0.1);
        }
      }

      // Camera: metro overview, or a chase view along the active corridor that
      // lifts slightly wherever the route is elevated over another road.
      const focusLane = state.focusedPathIndex;
      if (!prefersReducedMotion && focusLane !== null && curves[focusLane]) {
        const runner = tokens[state.currentPlayerIndex];
        const curve = curves[focusLane];
        const laneTiles = activePathTiles[focusLane] ?? [];
        const u = tileProgress(runner ? runner.displayPosition : 0, laneTiles.length);
        curve.getPointAt(u, scratchPoint);
        curve.getTangentAt(u, scratchTangent);

        // Keep the pawn centered in the zoom. The camera sits just behind and
        // above it, while the sightline stays on the pawn itself—not a point
        // farther down the road.
        const lift = (isCompact ? 19 : 15) + scratchPoint.y * 1.35;
        desiredPosition.set(scratchPoint.x - scratchTangent.x * 17, scratchPoint.y + lift, scratchPoint.z - scratchTangent.z * 17);
        desiredTarget.set(scratchPoint.x, scratchPoint.y + 1.25, scratchPoint.z);
      } else {
        desiredPosition.copy(overviewPosition);
        desiredTarget.copy(overviewTarget);
      }
      if (prefersReducedMotion) {
        camera.position.copy(desiredPosition);
        currentTarget.copy(desiredTarget);
      } else {
        const damping = 1 - Math.exp(-delta * 10);
        camera.position.lerp(desiredPosition, damping);
        currentTarget.lerp(desiredTarget, damping);
      }
      camera.lookAt(currentTarget);

      // Keep map signage legible regardless of camera distance.
      const distance = camera.position.distanceTo(currentTarget);
      const labelScale = THREE.MathUtils.clamp(distance / 190, 0.55, 1.35);
      labelSprites.forEach((sprite, index) => {
        const base = MAP_LABELS[index].kind === 'place' ? 20 : 11;
        sprite.scale.set(base * labelScale, base * labelScale / 2, 1);
      });
      renderer.render(scene, camera);
    };
    renderFrame();

    // ── Responsive sizing ───────────────────────────────────────────────────
    const handleResize = () => {
      const width = container.clientWidth;
      const height = container.clientHeight;
      if (width === 0 || height === 0) return;
      camera.aspect = width / height;
      camera.updateProjectionMatrix();
      renderer.setSize(width, height);
    };
    const resizeObserver = new ResizeObserver(handleResize);
    resizeObserver.observe(container);
    return () => {
      cancelAnimationFrame(animationId);
      resizeObserver.disconnect();
      disposables.forEach(item => item.dispose());
      trunkGeometry.dispose();
      crownGeometry.dispose();
      renderer.dispose();
      if (renderer.domElement.parentNode === container) {
        container.removeChild(renderer.domElement);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [laneKey]);
  return <div ref={containerRef} className="absolute inset-0" role="img" aria-label={`Atlanta road map game board. Player routes follow ${ATLANTA_ROUTES.map(route => route.label).join(', ')}, crossing at the Downtown interchange.`} data-id="element-262" data-mp="1csrux1udiyis" />;
}